import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Sparkles, Refrigerator, Heart, Users, History, Settings2,
  Plus, Trash2, Crown, LogOut, X,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/constants/brand";

const TABS = [
  { id: "prefs", label: "Preferences", icon: Settings2 },
  { id: "pantry", label: "Pantry", icon: Refrigerator },
  { id: "favorites", label: "Favorites", icon: Heart },
  { id: "households", label: "Households", icon: Users },
  { id: "history", label: "History", icon: History },
];

export function Sidebar({ open, onClose, onOpenPremium, onLoadConversation, onCookRecipe, onSuggestDinner, refreshKey }) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("prefs");
  const premium = user && user.is_premium;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-espresso/40 backdrop-blur-sm lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`fixed left-0 top-0 z-40 flex h-full w-[300px] flex-col border-r border-espresso/10 bg-cream transition-transform duration-300 lg:static lg:z-0 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-5 py-5">
          <div className="flex items-center gap-2">
            <img src={BRAND.logo} alt="Aunt Lorettas Kitchen Chat" className="h-11 w-11 rounded-full object-contain" />
            <div>
              <p className="font-serif text-base font-bold leading-none text-espresso">Aunt Lorettas Kitchen Chat</p>
              <p className="text-xs tracking-wide text-loretta-red">{BRAND.tagline}</p>
            </div>
          </div>
          <button className="lg:hidden" onClick={onClose} data-testid="sidebar-close">
            <X className="h-5 w-5 text-espressolt" />
          </button>
        </div>

        {!premium ? (
          <PremiumTeaser onOpenPremium={onOpenPremium} />
        ) : (
          <>
            <div className="px-4 pb-2">
              <button
                onClick={onSuggestDinner}
                data-testid="suggest-dinner-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-loretta-red py-2.5 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-loretta-redhover"
              >
                <Sparkles className="h-4 w-4" /> Suggest tonight's dinner
              </button>
            </div>
            <div className="flex gap-1 overflow-x-auto px-3 pb-2">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`sidebar-tab-${t.id}`}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    tab === t.id ? "bg-loretta-red text-white" : "text-espressolt hover:bg-creamalt"
                  }`}
                >
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {tab === "prefs" && <PrefsPanel refreshKey={refreshKey} />}
              {tab === "pantry" && <PantryPanel refreshKey={refreshKey} />}
              {tab === "favorites" && <FavoritesPanel onCookRecipe={onCookRecipe} refreshKey={refreshKey} />}
              {tab === "households" && <HouseholdsPanel refreshKey={refreshKey} />}
              {tab === "history" && <HistoryPanel onLoadConversation={onLoadConversation} refreshKey={refreshKey} />}
            </div>
          </>
        )}

        {user && (
          <div className="border-t border-espresso/10 p-4">
            <div className="mb-3 flex items-center gap-2">
              {premium && <Crown className="h-4 w-4 text-loretta-red" />}
              <span className="truncate text-sm text-espressolt">{user.email}</span>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-espresso/15 py-2 text-sm text-espressolt transition-colors hover:bg-creamalt"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

function PremiumTeaser({ onOpenPremium }) {
  const { user } = useAuth();
  return (
    <div className="flex flex-1 flex-col justify-between p-5">
      <div className="rounded-3xl bg-loretta-olive p-5 text-cream">
        <Crown className="mb-3 h-7 w-7 text-loretta-accent" />
        <h3 className="font-serif text-xl font-bold text-cream">Unlock Loretta's Memory</h3>
        <p className="mt-2 text-sm text-cream/85">
          Saved preferences, chat history, pantry memory, favorite recipes, household profiles &
          personalized picks.
        </p>
        <button
          onClick={onOpenPremium}
          data-testid="sidebar-upgrade-button"
          className="mt-4 w-full rounded-full bg-loretta-accent py-2.5 text-sm font-semibold text-espresso transition-colors hover:bg-white"
        >
          Go Premium
        </button>
      </div>
      {!user && (
        <p className="mt-4 text-center text-xs text-espresso/50">
          Free chat is unlimited — sign in to save your kitchen.
        </p>
      )}
    </div>
  );
}

function PrefsPanel({ refreshKey }) {
  const [p, setP] = useState({ diet: "", allergies: "", dislikes: "", cuisines: "", skill_level: "home cook" });
  useEffect(() => { api.get("/preferences").then(({ data }) => data && setP((prev) => ({ ...prev, ...data }))); }, [refreshKey]);
  const save = async () => { await api.put("/preferences", p); toast.success("Loretta will remember that, tesoro."); };
  const field = (key, label, ph) => (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-espresso/50">{label}</label>
      <input
        value={p[key] || ""}
        onChange={(e) => setP({ ...p, [key]: e.target.value })}
        placeholder={ph}
        data-testid={`pref-${key}`}
        className="w-full rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red"
      />
    </div>
  );
  return (
    <div>
      {field("diet", "Diet", "e.g. vegetarian")}
      {field("allergies", "Allergies", "e.g. peanuts, shellfish")}
      {field("dislikes", "Dislikes", "e.g. cilantro")}
      {field("cuisines", "Loves", "e.g. Italian, Thai")}
      {field("skill_level", "Skill level", "home cook")}
      <button onClick={save} data-testid="save-prefs-button" className="mt-1 w-full rounded-full bg-loretta-red py-2 text-sm font-semibold text-white hover:bg-loretta-redhover">Save preferences</button>
    </div>
  );
}

function PantryPanel({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState("");
  useEffect(() => { api.get("/pantry").then(({ data }) => setItems(data || [])); }, [refreshKey]);
  const add = async () => { if (!val.trim()) return; const { data } = await api.post("/pantry", { item: val.trim() }); setItems(data); setVal(""); };
  const remove = async (it) => { const { data } = await api.delete(`/pantry/${encodeURIComponent(it)}`); setItems(data); };
  return (
    <div>
      <div className="mb-3 flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add ingredient" data-testid="pantry-input" className="flex-1 rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red" />
        <button onClick={add} data-testid="pantry-add-button" className="rounded-xl bg-loretta-red px-3 text-white hover:bg-loretta-redhover"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.length === 0 && <p className="text-sm text-espresso/50">Your pantry is empty. Add what you've got!</p>}
        {items.map((it) => (
          <span key={it} className="flex items-center gap-1 rounded-full bg-creamalt px-3 py-1 text-sm text-espresso">
            {it}
            <button onClick={() => remove(it)} className="text-espresso/40 hover:text-loretta-red"><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function FavoritesPanel({ onCookRecipe, refreshKey }) {
  const [favs, setFavs] = useState([]);
  useEffect(() => { api.get("/favorites").then(({ data }) => setFavs(data || [])); }, [refreshKey]);
  const remove = async (id) => { const { data } = await api.delete(`/favorites/${id}`); setFavs(data); };
  if (favs.length === 0) return <p className="text-sm text-espresso/50">No saved recipes yet. Tap the heart on any recipe card.</p>;
  return (
    <div className="space-y-2">
      {favs.map((f) => (
        <div key={f.id} className="flex items-center justify-between rounded-2xl bg-creamalt px-3 py-2">
          <button onClick={() => onCookRecipe(f.recipe)} className="flex-1 truncate text-left text-sm font-medium text-espresso hover:text-loretta-red">{f.title}</button>
          <button onClick={() => remove(f.id)} className="text-espresso/40 hover:text-loretta-red"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

function HouseholdsPanel({ refreshKey }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", adults: 2, kids: 0, notes: "" });
  useEffect(() => { api.get("/households").then(({ data }) => setList(data || [])); }, [refreshKey]);
  const add = async () => { if (!form.name.trim()) return; const { data } = await api.post("/households", form); setList(data); setForm({ name: "", adults: 2, kids: 0, notes: "" }); };
  const remove = async (id) => { const { data } = await api.delete(`/households/${id}`); setList(data); };
  return (
    <div>
      <div className="mb-4 space-y-2 rounded-2xl bg-creamalt p-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Household name" data-testid="household-name" className="w-full rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red" />
        <div className="flex gap-2">
          <input type="number" min="0" value={form.adults} onChange={(e) => setForm({ ...form, adults: +e.target.value })} placeholder="Adults" className="w-1/2 rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red" />
          <input type="number" min="0" value={form.kids} onChange={(e) => setForm({ ...form, kids: +e.target.value })} placeholder="Kids" className="w-1/2 rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red" />
        </div>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (e.g. gluten-free)" className="w-full rounded-xl border border-espresso/15 bg-white px-3 py-2 text-sm outline-none focus:border-loretta-red" />
        <button onClick={add} data-testid="household-add-button" className="w-full rounded-full bg-loretta-red py-2 text-sm font-semibold text-white hover:bg-loretta-redhover">Add household</button>
      </div>
      <div className="space-y-2">
        {list.map((h) => (
          <div key={h.id} className="flex items-center justify-between rounded-2xl bg-creamalt px-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-espresso">{h.name}</p>
              <p className="text-xs text-espresso/50">{h.adults} adults · {h.kids} kids{h.notes ? ` · ${h.notes}` : ""}</p>
            </div>
            <button onClick={() => remove(h.id)} className="text-espresso/40 hover:text-loretta-red"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryPanel({ onLoadConversation, refreshKey }) {
  const [convos, setConvos] = useState([]);
  useEffect(() => { api.get("/conversations").then(({ data }) => setConvos(data || [])); }, [refreshKey]);
  if (convos.length === 0) return <p className="text-sm text-espresso/50">No saved conversations yet. Start chatting!</p>;
  return (
    <div className="space-y-2">
      {convos.map((c) => (
        <button key={c.session_id} onClick={() => onLoadConversation(c.session_id)} data-testid="history-item" className="block w-full rounded-2xl bg-creamalt px-3 py-2 text-left hover:bg-loretta-accent/50">
          <p className="truncate text-sm font-medium text-espresso">{c.title}</p>
          <p className="text-xs text-espresso/50">{c.message_count} messages</p>
        </button>
      ))}
    </div>
  );
}
